import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Send, FileText, Check, Printer, Save, Clock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { FileUploadInline, OcrImageUpload } from "@/components/ImageUpload";
import SignaturePad from "@/components/SignaturePad";

function FieldRenderer({ field, value, onChange }) {
  const required = field.is_required;
  switch (field.field_type) {
    case "textarea":
      return <Textarea data-testid={`field-${field.id}`} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} required={required} />;
    case "number":
      return <Input data-testid={`field-${field.id}`} type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} required={required} />;
    case "email":
      return <Input data-testid={`field-${field.id}`} type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} required={required} />;
    case "phone":
      return <Input data-testid={`field-${field.id}`} type="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} required={required} />;
    case "date":
      return <Input data-testid={`field-${field.id}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} />;
    case "select":
      return (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`field-${field.id}`}><SelectValue placeholder={field.placeholder || "اختر..."} /></SelectTrigger>
          <SelectContent>
            {field.options.map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2" data-testid={`field-${field.id}`}>
          {field.options.map((o, i) => (
            <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={arr.includes(o)}
                onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))}
              />
              {o}
            </label>
          ))}
        </div>
      );
    }
    case "radio":
      return (
        <RadioGroup value={value || ""} onValueChange={onChange} data-testid={`field-${field.id}`}>
          {field.options.map((o, i) => (
            <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
              <RadioGroupItem value={o} id={`${field.id}-${i}`} />
              {o}
            </label>
          ))}
        </RadioGroup>
      );
    case "file":
      return <FileUploadInline value={value && typeof value === "object" ? value : null} onChange={onChange} testid={`field-${field.id}`} />;
    case "ocr_image":
      return (
        <OcrImageUpload
          value={value && typeof value === "object" ? value : null}
          onChange={onChange}
          testid={`field-${field.id}`}
          ocrPrompt={field.ocr_prompt || ""}
        />
      );
    case "signature":
      return <SignaturePad value={value && typeof value === "object" ? value : null} onChange={onChange} testid={`field-${field.id}`} />;
    default:
      return <Input data-testid={`field-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} required={required} />;
  }
}

export default function FillFormPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;

  const [templates, setTemplates] = useState([]);
  const [tmpl, setTmpl] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [editSubmission, setEditSubmission] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      setLoadingEdit(true);
      api.get(`/submissions/${editId}`)
        .then((r) => {
          const sub = r.data;
          setEditSubmission(sub);
          setTmpl(sub.template);
          setValues(sub.values || {});
          setLoadingEdit(false);
        })
        .catch((e) => {
          toast.error(formatApiError(e));
          navigate("/submissions");
        });
    } else {
      api.get("/form-templates", { params: { active_only: true } })
        .then((r) => setTemplates(r.data))
        .catch(() => {});
    }
  }, [editId, isEditMode, navigate]);

  const selectTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    setTmpl(t);
    setValues({});
    setSuccess(null);
  };

  const submit = async (status) => {
    if (!tmpl) return;
    setSubmitting(true);
    try {
      let data;
      if (isEditMode && editSubmission) {
        const res = await api.put(`/submissions/${editSubmission.id}`, { values, status });
        data = res.data;
      } else {
        const res = await api.post("/submissions", { form_template_id: tmpl.id, values, status });
        data = res.data;
      }
      if (status === "draft") {
        toast.success("تم الحفظ كمسودة");
        navigate("/submissions");
      } else {
        setSuccess(data);
        toast.success(isEditMode ? "تم تحديث الاستمارة وإرسالها" : "تم إرسال الاستمارة");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEdit) {
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }

  if (success) {
    return (
      <div data-testid="submission-success" className="max-w-2xl mx-auto">
        <div className="gov-card p-10 text-center animate-slide-up">
          <div className="w-16 h-16 rounded-full bg-success/10 text-success mx-auto flex items-center justify-center mb-4">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">تم الإرسال بنجاح</h1>
          <p className="text-muted-foreground mb-6">احفظ رقم الاستمارة أو امسح QR للوصول السريع</p>
          <div className="bg-surface rounded-sm p-6 mb-6">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">رقم الاستمارة</div>
            <div data-testid="success-form-number" className="font-mono-num font-bold text-3xl text-primary mb-4">{success.form_number}</div>
            <div className="flex justify-center">
              <div className="bg-white p-3 border border-border rounded-sm">
                <QRCodeSVG value={success.qr_code_data} size={180} />
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate(`/print/${success.id}`)}>
              <Printer className="w-4 h-4 ms-1" /> طباعة PDF
            </Button>
            <Button onClick={() => { setSuccess(null); setTmpl(null); setValues({}); }} className="bg-primary hover:bg-primary/90">
              استمارة جديدة
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!tmpl) {
    return (
      <div data-testid="fill-form-list">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight">تعبئة استمارة</h1>
          <p className="text-muted-foreground mt-1">اختر النموذج المطلوب لتعبئته</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTemplate(t.id)}
              data-testid={`select-template-${t.id}`}
              className="text-start gov-card p-5 hover:border-primary/40 transition-all"
            >
              <div className="w-10 h-10 rounded-sm bg-accent text-primary flex items-center justify-center mb-3">
                <FileText className="w-5 h-5" />
              </div>
              <div className="font-display font-semibold text-lg mb-1">{t.title}</div>
              <p className="text-sm text-muted-foreground line-clamp-2">{t.description || "—"}</p>
              <div className="text-xs text-muted-foreground mt-3">{t.fields?.length || 0} حقل</div>
            </button>
          ))}
          {!templates.length && (
            <div className="col-span-full gov-card p-12 text-center text-muted-foreground">
              لا توجد نماذج نشطة حالياً
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="fill-form-page" className="max-w-3xl mx-auto">
      <button
        onClick={() => isEditMode ? navigate("/submissions") : setTmpl(null)}
        className="text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        ← {isEditMode ? "رجوع للاستمارات" : "رجوع لقائمة النماذج"}
      </button>

      {isEditMode && editSubmission && (
        <div className="mb-4 px-4 py-2 rounded-sm bg-info/10 text-info text-sm flex items-center gap-2">
          <Clock className="w-4 h-4" />
          تعديل مسودة — رقم الاستمارة: <span className="font-mono font-bold">{editSubmission.form_number}</span>
        </div>
      )}

      <div className="gov-card p-8">
        <h1 className="font-display text-3xl font-bold mb-1">{tmpl.title}</h1>
        {tmpl.description && <p className="text-muted-foreground mb-6">{tmpl.description}</p>}
        <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
          {(tmpl.fields || []).map((f) => (
            <div key={f.id} className="space-y-1.5">
              <Label className="flex items-center gap-1">
                {f.label}
                {f.is_required && <span className="text-destructive">*</span>}
              </Label>
              <FieldRenderer field={f} value={values[f.id]} onChange={(v) => setValues({ ...values, [f.id]: v })} />
            </div>
          ))}

          {/* Status selection */}
          <div className="border-t border-border pt-5">
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => isEditMode ? navigate("/submissions") : setTmpl(null)}
              >
                إلغاء
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                data-testid="save-draft-button"
                onClick={() => submit("draft")}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Save className="w-4 h-4 ms-2" />}
                حفظ كمسودة (قيد الانتظار)
              </Button>
              <Button
                type="button"
                disabled={submitting}
                data-testid="submit-form-button"
                onClick={() => submit("submitted")}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Send className="w-4 h-4 ms-2" />}
                إرسال مكتملاً
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-end">
              المسودة تُحفظ ويمكن تعديلها لاحقاً من صفحة الاستمارات
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
